import { DownOutlined } from '@ant-design/icons'
import { Tabs } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getRoleTabIds, RoleTabId } from '../../utils/roleTabs'

type RoleTabsProps = {
  activeTab: RoleTabId
  onChange: (tab: RoleTabId) => void
  // Opt in to the two group-scoped tabs (Group Admin / Group Manager). The
  // single-site list omits this and renders only its seven tabs.
  includeGroupRoles?: boolean
  testId?: string
}

const ROLE_TAB_LABEL_KEYS: Record<RoleTabId, string> = {
  [RoleTabId.AllMembers]: reportsConstants.TEAM_LIST_TAB_ALL_MEMBERS,
  [RoleTabId.GroupAdmin]: reportsConstants.TEAM_LIST_TAB_GROUP_ADMIN,
  [RoleTabId.GroupManager]: reportsConstants.TEAM_LIST_TAB_GROUP_MANAGER,
  [RoleTabId.Admin]: reportsConstants.TEAM_LIST_TAB_ADMIN,
  [RoleTabId.Management]: reportsConstants.TEAM_LIST_TAB_MANAGEMENT,
  [RoleTabId.SeniorCarer]: reportsConstants.TEAM_LIST_TAB_SENIOR_CARER,
  [RoleTabId.Carer]: reportsConstants.TEAM_LIST_TAB_CARER,
  [RoleTabId.ItSecurity]: reportsConstants.TEAM_LIST_TAB_IT_SECURITY,
  [RoleTabId.AuditView]: reportsConstants.TEAM_LIST_TAB_AUDIT_VIEW
}

const StyledTabsWrapper = styled.div`
  padding: 0 24px;
  background-color: ${(props) => props.theme.palette.white};
  border-bottom: 1px solid #f0f0f0;

  .ant-tabs-nav {
    margin-bottom: 0 !important;
  }

  .ant-tabs-tab {
    padding: 12px 0 !important;
  }

  .ant-tabs-tab .ant-tabs-tab-btn {
    font-size: 16px;
    line-height: 24px;
    color: rgba(0, 0, 0, 0.85);
  }

  /* Mobile: the 7+ tabs don't fit, so swap antd's overflow "more" dropdown for a
     horizontally scrollable strip (CM-3670, per the 320px frame). Disable the
     transform-based paging and hide the operations button so native scroll works. */
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 0 16px;

    /* antd's own nav-wrap/operations rules are higher-specificity, so these
       need !important to win (matching the !important overrides above). */
    .ant-tabs-nav-wrap {
      overflow-x: auto !important;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .ant-tabs-nav-list {
      transform: none !important;
    }

    .ant-tabs-nav-operations {
      display: none !important;
    }
  }
`

const RoleTabs: React.FC<RoleTabsProps> = ({
  activeTab,
  onChange,
  includeGroupRoles = false,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)

  const items = getRoleTabIds(includeGroupRoles).map((id) => ({
    key: id,
    label: t(ROLE_TAB_LABEL_KEYS[id])
  }))

  return (
    <StyledTabsWrapper data-testid={testId}>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => onChange(key as RoleTabId)}
        items={items}
        moreIcon={<DownOutlined />}
      />
    </StyledTabsWrapper>
  )
}

export default RoleTabs
